import {useEffect, useState} from 'react';
import {HttpService} from '../http/Http.service';
import {HttpErrorService} from '../http/HttpError.service';
import {Page, SpringPageable, StatusCodeE} from '../../model/models';

interface UseTableDataProps {
    simulatorId: string | undefined;
    status: StatusCodeE;
    initialPageSize?: number;
    filterHasPagoPaOnly?: boolean;
    ignoreSendIntermediateStatuses?: boolean;
    filterHasProtocolOnly?: boolean,
    sendStatusFilter?: string;
}

export function usePaginatedData<T>({
    simulatorId,
    status,
    initialPageSize = 20,
    filterHasPagoPaOnly,
    ignoreSendIntermediateStatuses,
    filterHasProtocolOnly,
    sendStatusFilter,
}: UseTableDataProps): {
    data: T[];
    totalItems: number;
    pageData: Page;
    searchText: string;
    setSearchText: (value: (((prevState: string) => string) | string)) => void;
    sortField: string | undefined;
    sortOrder: string | undefined;
    loadData: (page?: number, pageSize?: number, sortFieldParam?: string, sortOrderParam?: string, searchQuery?: string) => Promise<void>;
    handleTableChange: (pagination: any, filters: any, sorter: any) => void;
    reloadCurrentPage: () => Promise<void>
} {
    const [data, setData] = useState<T[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [pageData, setPageData] = useState<Page>({
        current: 1,
        pageSize: initialPageSize,
    });

    const [searchText, setSearchText] = useState<string>('');
    const [searchInit, setSearchInit] = useState<boolean>(false);
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [sortField, setSortField] = useState<string | undefined>();
    const [sortOrder, setSortOrder] = useState<string | undefined>();

    // Unified data loading function
    const loadData = async (
        page?: number,
        pageSize?: number,
        sortFieldParam?: string,
        sortOrderParam?: string,
        searchQuery?: string
    ) => {
        if (!simulatorId) return;

        const currentPage = page !== undefined ? page : pageData.current - 1;
        const currentPageSize = pageSize !== undefined ? pageSize : pageData.pageSize;
        const currentSortField = sortFieldParam;
        const currentSortOrder = sortOrderParam ;
        const query = searchQuery !== undefined ? searchQuery : searchText;

        const requestPage: SpringPageable = {
            page: currentPage,
            size: currentPageSize,
            sort: currentSortField && currentSortOrder ? `${currentSortField},${currentSortOrder}` : undefined,
        };

        const endpoint = query.length >= 3
            ? `/simulazione/${simulatorId}/file-ts/status/${status}/${query}`
            : `/simulazione/${simulatorId}/file-ts/status/${status}/ALL`;

        try {
            const response = await HttpService.getInstanceAxios().get(endpoint, {
                params: {
                    ...requestPage,
                    hasPaymentOnly: filterHasPagoPaOnly || false,
                    hasProtocolOnly: filterHasProtocolOnly || false,
                    ignoreSendIntermediateStatuses: ignoreSendIntermediateStatuses || false,
                    sendStatus: sendStatusFilter || undefined,
                }
            });


            const responseData = response.data || {};
            setData(responseData.content || []);
            setTotalItems(responseData.totalElements || 0);
            setPageData({
                current: (responseData.number || 0) + 1,
                pageSize: responseData.size || currentPageSize,
            });
        } catch (error) {
            HttpErrorService.handle('Errore durante il caricamento dei dati.', error, true);
        }
    };

    const handleTableChange = (pagination: any, filters: any, sorter: any) => {
        console.log(`handleTableChange: ${sorter.field} ${sorter.order}`)
        const newSortField = sorter.field;
        const newSortOrder = sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : undefined;
        const newPage = pagination.current - 1;
        const newPageSize = pagination.pageSize;

        setSortField(newSortField);
        setSortOrder(newSortOrder);

        loadData(newPage, newPageSize, newSortField, newSortOrder);
    };

    const reloadCurrentPage = async () => {
        await loadData(
            pageData.current - 1,
            pageData.pageSize,
            sortField,
            sortOrder,
            searchText
        );
    };

    // Debounced search effect
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchText);
        }, 500);

        return () => clearTimeout(handler);
    }, [searchText]);

    // Search effect
    useEffect(() => {
        if (debouncedSearch.length >= 3) {
            setSearchInit(true);
            loadData(0, pageData.pageSize, sortField, sortOrder, debouncedSearch);
        } else if (debouncedSearch.length === 0 && searchInit) {
            loadData(0, pageData.pageSize, sortField, sortOrder, debouncedSearch);
        }
    }, [debouncedSearch, pageData.pageSize, sortField, sortOrder, searchInit]);

    // Initial data load
    useEffect(() => {
        loadData(0, pageData.pageSize);
    }, []);

    return {
        data,
        totalItems,
        pageData,
        searchText,
        setSearchText,
        sortField,
        sortOrder,
        loadData,
        handleTableChange,
        reloadCurrentPage,
    };
}
